import React, {
  ReactNode,
  createContext,
  useState,
  useContext,
  SetStateAction,
  Dispatch,
} from 'react';
import { useSearch } from '@tanstack/react-router';

const TableProgressContext = createContext<[number, Dispatch<SetStateAction<number>>] | undefined>(
  undefined
);

/**
 * TableProgressProvider - Tracks table row progress
 *
 * Synchronously derives the visible row index from the URL's rowIndex search
 * param so that consumers receive the correct value on the very first render —
 * avoiding the async useEffect timing window that caused the first-time scroll
 * restoration bug (EDP-2426).
 */
const TableProgressProvider = ({ children }: { children: ReactNode }) => {
  const [progress, setProgress] = useState(0);
  const search = useSearch({ strict: false }) as { rowIndex?: string };
  const urlRowIndex = search.rowIndex != null ? parseInt(search.rowIndex, 10) : null;
  // When rowIndex is present in the URL, use it directly (synchronous); otherwise
  // fall back to the stored progress value (set by onExecuteSearch / onSearch).
  const effectiveProgress = urlRowIndex !== null && !isNaN(urlRowIndex) ? urlRowIndex : progress;

  return (
    <TableProgressContext.Provider value={[effectiveProgress, setProgress]}>
      {children}
    </TableProgressContext.Provider>
  );
};

/**
 * Saves the row index of the last visited dashboard
 */
function useTableProgress() {
  const context = useContext(TableProgressContext);
  if (context === undefined) {
    throw new Error('useTableProgress must be used within a TableProgressProvider');
  }
  return context;
}

export { TableProgressProvider, useTableProgress };
